import { lazy } from "react";
import { BarChart3, FileSearch, FileText, LayoutGrid, Shield, Table2 } from "lucide-react";

import type { TabRoute } from "@/lib/routes";

/**
 * Energy Advisor customer detail tabs. The Bill Analyzer and Contract
 * Analyzer tabs each own their list / detail switch via the `?analysis={id}`
 * search param so the tab path stays flat (no nested `<RouterTabs>` layer).
 * The accounts grid lives inside the Snapshot tab (AB#7747), not as its own
 * tab.
 */
const EnergyAdvisorSnapshotTab = lazy(() => import("./tabs/EnergyAdvisorSnapshotTab"));
const EnergyAdvisorClientProfileTab = lazy(
  () => import("./tabs/EnergyAdvisorClientProfileTab"),
);
const EnergyAdvisorRfqsTab = lazy(() => import("./tabs/EnergyAdvisorRfqsTab"));
const EnergyAdvisorPortfolioAnalyzerTab = lazy(
  () => import("./tabs/EnergyAdvisorPortfolioAnalyzerTab"),
);
const EnergyAdvisorContractAnalyzerTab = lazy(
  () => import("./tabs/EnergyAdvisorContractAnalyzerTab"),
);
const EnergyAdvisorMatrixPricingTab = lazy(
  () => import("./tabs/EnergyAdvisorMatrixPricingTab"),
);

export const ENERGY_ADVISOR_CUSTOMER_TAB_ROUTES: ReadonlyArray<TabRoute> = [
  {
    path: "client-profile",
    label: "Client Profile",
    icon: Shield,
    element: <EnergyAdvisorClientProfileTab />,
  },
  {
    path: "portfolio",
    label: "Bill Analyzer",
    icon: LayoutGrid,
    element: <EnergyAdvisorPortfolioAnalyzerTab />,
    requiredPermission: "customers.portfolio_analyzer.manage",
  },
  {
    // Presentation-only — contracts are uploaded on the client's Supply
    // Contracts tab; this tool runs and compares analyses against them.
    path: "contract-analyzer",
    label: "Contract Analyzer",
    icon: FileSearch,
    element: <EnergyAdvisorContractAnalyzerTab />,
    requiredPermission: "customers.contract_analyzer.manage",
  },
  {
    path: "snapshot",
    label: "Snapshot",
    icon: BarChart3,
    element: <EnergyAdvisorSnapshotTab />,
  },
  {
    path: "rfqs",
    label: "RFQs",
    icon: FileText,
    element: <EnergyAdvisorRfqsTab />,
    // The RFQ walkthrough (`rfqs/:rfqId`) is its own chromeless route, not a
    // child of this tab — this keeps the sidebar's RFQs row lit on it.
    activeOnSubpaths: true,
  },
  {
    path: "matrix-pricing",
    label: "Matrix Pricing",
    icon: Table2,
    element: <EnergyAdvisorMatrixPricingTab />,
  },
];
